import { useCallback, createElement, useState, useEffect } from 'react';
import 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import _isEmpty from 'lodash/isEmpty';
import { useDevice } from '@atc/react-device';
import { BrowserStorage } from '@atc/browser-storage';
import { placeholderImages } from 'axl-config';
import { Button, AlertAutoClose, Text, ColoredBackground, Glyphicon, Toggle, SubHeading, OverlayTrigger, Popover } from 'reaxl';
import { sendClick, DelayedImpressionObserver, sendImpressions } from 'reaxl-analytics';
import { myWalletCalculatePaymentsCTAClick, myWalletSummaryLinkClick, myWalletViewDealStatusCTAClick, myWalletGridInlineClick, myWalletGridTradeInIndicator } from 'reaxl-analytics-handlers';
import { useFeatures } from '@atc/features';
import DealBanner from '../DealBanner/DealBanner.js';
import MyWalletWelcomeMessage from '../MyWalletWelcomeMessage/MyWalletWelcomeMessage.js';
import SyncMyWalletAlert from '../SyncMyWalletAlert/SyncMyWalletAlert.js';
import WalletServicesErrorAlert from '../WalletServicesErrorAlert/WalletServicesErrorAlert.js';
import MyWalletGrid from './MyWalletGrid.js';
import MyWalletGridLight from './MyWalletGridLight.js';
import Logo from './Logo.js';
import Title from './Title.js';
import { jsx, jsxs } from 'react/jsx-runtime';
const browserNotificationPromptCache = /* #__PURE__*/new BrowserStorage('OFFSITE_BROWSER_NOTIFS_PROMPT', {
  persist: true
});
const myWalletInfoPopover = function () {
  let glyphiconSize = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '200';
  const textSizeClass = 'text-size-' + glyphiconSize;
  return /*#__PURE__*/jsx(OverlayTrigger, {
    overlay: /*#__PURE__*/jsx(Popover, {
      id: "wallet-popover",
      children: "See payments tailored to you, estimated loan rates, and budget tools to customize your shopping experience."
    }),
    placement: "top",
    trigger: ['hover', 'focus', 'click'],
    rootClose: true,
    children: /*#__PURE__*/jsx(Glyphicon, {
      glyph: "info",
      className: 'margin-left-1 text-subdued-muted cursor-pointer ' + textSizeClass
    })
  });
};
function DealNotificationSection(_ref) {
  let {
    inventoryId
  } = _ref;
  // NOTE: Part of US1281773: Wallet deal notifications fake door test, clean up once complete
  const browserNotifsPrompted = browserNotificationPromptCache.getItem()?.dealsPrompted;
  const [showPrompt, setShowPrompt] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  useEffect(() => {
    if (!browserNotifsPrompted) {
      setShowPrompt(true);
      sendImpressions({
        name: 'offsiteBrowserPushNotificationImpression',
        data: {
          location: 'nds',
          inventoryId
        }
      });
    }
  }, [browserNotifsPrompted, inventoryId]);
  const handleToggleChange = () => {
    setShowAlert(true);
    browserNotificationPromptCache.setItem({
      dealsPrompted: true
    });
    setShowPrompt(false);
    sendClick('offsiteBrowserPushNotificationClick', {}, {
      location: 'nds',
      inventoryId
    });
  };
  if (!showPrompt && !showAlert) {
    return null;
  }
  if (showAlert) {
    return /*#__PURE__*/jsxs(AlertAutoClose, {
      className: "margin-vertical-2 margin-horizontal-2",
      show: true,
      sticky: false,
      bsStyle: "success",
      timeout: 8000,
      children: [/*#__PURE__*/jsx(Text, {
        weight: "bold",
        children: "Thank you for your interest!"
      }), " We look forward to bringing this feature to you."]
    });
  }
  return /*#__PURE__*/jsxs(ColoredBackground, {
    className: "display-flex padding-vertical-4 justify-content-center align-items-center",
    "data-cmp": "dealReceiptBrowserNotification",
    invertText: true,
    uiContext: "dark-gray",
    children: [/*#__PURE__*/jsx(Glyphicon, {
      className: "margin-right-2 margin-bottom-1",
      glyph: "bell"
    }), /*#__PURE__*/jsx(Text, {
      weight: "bold",
      children: "Allow Notifications"
    }), /*#__PURE__*/jsx(Toggle, {
      checked: showAlert,
      onChange: handleToggleChange,
      className: "margin-left-2",
      "aria-label": "Allow Notifications"
    })]
  });
}
function Description(_ref2) {
  let {
    isXs = false,
    trigger,
    withKbbGridUpdate = false
  } = _ref2;
  return /*#__PURE__*/jsxs("div", {
    className: "display-flex margin-top-3 margin-top-sm-0",
    children: [/*#__PURE__*/jsx(SubHeading, {
      level: isXs ? '3' : '1',
      color: withKbbGridUpdate ? 'gray-base' : 'primary',
      children: "Find your vehicle faster"
    }), /*#__PURE__*/jsx(SubHeading, {
      level: isXs ? '3' : '1',
      color: withKbbGridUpdate ? 'gray-base' : 'accent',
      className: isXs ? 'margin-left-1' : 'margin-left-2',
      children: "and within budget."
    }), myWalletInfoPopover(500)]
  });
}
function MyWalletGridContainer(_ref3) {
  let {
    children,
    ctaBtnStyle = 'primary',
    hideGrid = false,
    inventoryId,
    isRow,
    paymentsDuck,
    sectionComponentName = 'PageSection',
    sectionProps = {},
    showWelcomeMessage = false,
    uiContext = 'default',
    onGridClick = () => {},
    onUpdateMyWallet = () => {},
    displayWalletErrorMessage = false,
    showIcon = true,
    onContactOwnerClick = () => {}
  } = _ref3;
  const dispatch = useDispatch();
  const updateMyWallet = useCallback(data => {
    onUpdateMyWallet(data);
    dispatch(paymentsDuck.creators.updateMyWallet(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const updateMyWalletInteractions = useCallback(data => dispatch(paymentsDuck.creators.updateMyWalletInteractions(data)), [dispatch, paymentsDuck.creators]);
  const displayModal = useSelector(paymentsDuck.selectors.getDisplayMyWalletModal);
  const paymentEmphasis = useSelector(state => paymentsDuck.selectors.getMyWalletInteraction(state, 'paymentEmphasis'));
  const hasOpenedTradeIn = useSelector(state => paymentsDuck.selectors.getMyWalletInteraction(state, 'hasOpenedTradeIn'));
  const paymentsInfo = useSelector(state => paymentsDuck?.selectors?.getPaymentsInfo?.(state));
  const dealDetails = useSelector(state => paymentsDuck?.selectors?.getTransformedDealDetails(state));
  const paymentOption = useSelector(paymentsDuck.selectors.getPaymentOption);
  const walletIsLoading = useSelector(paymentsDuck.selectors.getIsLoading);
  const isPaymentsInfoLoaded = useSelector(paymentsDuck.selectors.getIsPaymentsInfoLoaded);
  const inventory = useSelector(state => state?.inventory?.[inventoryId]) || {};

  // inventory.owner can be undefined, null, owner id (number), or owner object that might have an id
  const inventoryOwner = inventory.owner;
  const ownerid = typeof inventoryOwner === 'object' ? inventoryOwner.id : inventoryOwner;
  const owner = useSelector(state => state?.owners?.[ownerid]) || {};
  const isPrivateSeller = owner.privateSeller;
  const showGrid = !hideGrid;
  const {
    walletId
  } = paymentsInfo;
  const {
    global_kbb_branded_wallet: [enableKbbBrandedWallet, enableWalletVariants = {}],
    my_wallet: [, {
      enable_deals: enableDeals
    }],
    srp_wallet_grid_update: [, {
      with_kbb_grid_upgrade: withKbbGridUpdate,
      higher_placement: placeWalletGridHigher
    }],
    offsite_browser_notifications: [enableOfsiteNotifications, {
      deal_prompt: enableDealPrompt
    }],
    srp_wallet_grid_horizontal_spacing_update: [horizontalSpacingUpdate]
  } = useFeatures(['global_kbb_branded_wallet', 'srp_wallet_grid_update', 'my_wallet', 'offsite_browser_notifications', 'srp_wallet_grid_horizontal_spacing_update']);
  const syncAlert = paymentsInfo?.interactions?.syncAlert;
  const showAlert = syncAlert?.showAlert;
  const dealIds = Object.keys(dealDetails);
  const existingDealId = dealIds.filter(dealId => dealDetails?.[dealId]?.vehicle?.vin === inventory.vin);
  const existingDeal = dealDetails[existingDealId] || {};
  const isEsntialDeal = inventory?.esntial && !inventory?.esntial.deemphasized && !existingDeal?.isAccelerate;
  let dealBannerUiContext = 'default';
  if (dealDetails?.[existingDealId]?.isEcommerce) {
    dealBannerUiContext = 'esntialComplete';
  } else if (isEsntialDeal) {
    dealBannerUiContext = 'esntial';
  }
  const dealVehicle = dealDetails?.[existingDealId]?.vehicle;
  const enableDealPanel = enableDeals && !_isEmpty(existingDealId);
  const enabledDealNotificationSection = enableDealPanel && enableOfsiteNotifications && enableDealPrompt;
  const {
    no_logo: disableLogo
  } = enableWalletVariants;
  const device = useDevice();
  const isLessThanLarge = device?.lessThan?.lg;
  const isXs = device?.is?.xs;
  const isLightGrid = uiContext === 'light';
  const enableTitleLink = true;
  const toggleModal = useCallback(panel => {
    updateMyWallet({
      displayModal: !displayModal,
      selectedPanelName: panel
    });
  }, [displayModal, updateMyWallet]);
  const handleUpdateButtonClick = useCallback((event, data) => {
    // Toggle open the modal to the main menu (panel = null)
    toggleModal(null);
    sendClick(myWalletCalculatePaymentsCTAClick, event, {
      ...data,
      paymentsDuck
    });
  }, [toggleModal, paymentsDuck]);
  const handleMyWalletGridClick = useCallback(function (event) {
    let data = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    const {
      panel
    } = data;
    if (panel === 'tradeIn' || panel === 'kbbValuation' || panel === 'valuationHistory') {
      if (!hasOpenedTradeIn) {
        updateMyWalletInteractions({
          hasOpenedTradeIn: true
        });
      }
    }
    toggleModal(panel);
    sendClick(myWalletSummaryLinkClick, event, {
      panel,
      inventoryId,
      isPsxVdp: isPrivateSeller
    });
    onGridClick(event, data);
  }, [onGridClick, toggleModal, hasOpenedTradeIn, updateMyWalletInteractions, isPrivateSeller, inventoryId]);
  const label = paymentEmphasis ? 'Update Payment Settings' : 'Calculate Payment';
  const walletIdLabel = function () {
    let showToolTip = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    return /*#__PURE__*/jsxs("span", {
      className: clsx('text-gray-dark margin-top-1 margin-bottom-0 text-size-200', {
        'margin-right-1': !horizontalSpacingUpdate,
        'margin-right-2': horizontalSpacingUpdate
      }),
      "data-cmp": "wallet-id",
      children: [/*#__PURE__*/jsx("span", {
        className: "text-bold",
        children: "My Wallet ID"
      }), ":\xA0", walletId, showToolTip && myWalletInfoPopover(200)]
    });
  };
  const handleMyWalletDashboardClick = event => {
    toggleModal(null);
    sendClick(myWalletGridInlineClick, event);
  };
  const handleMyWalletTitleClick = event => {
    toggleModal(null);
    sendClick(myWalletGridInlineClick, event, {
      enableTitleLink,
      inventoryId
    });
  };
  const handleMyWalletGridTradeInIndicatorImpression = () => {
    sendImpressions({
      name: myWalletGridTradeInIndicator,
      paymentsDuck
    });
  };
  const gridProps = {
    isRow,
    onClick: handleMyWalletGridClick,
    onTradeInIndicatorImpression: handleMyWalletGridTradeInIndicatorImpression,
    paymentsInfo,
    paymentOption,
    isLoading: !isPaymentsInfoLoaded || walletIsLoading,
    key: 'my-wallet-grid-key',
    updatedGrid: true,
    showIcon
  };
  const variantsDisabled = _isEmpty(enableWalletVariants) || !Object.values(enableWalletVariants).some(Boolean);
  const enableGlobalThemeStyles = enableKbbBrandedWallet && variantsDisabled;
  if (isLightGrid) {
    let content = showGrid && /*#__PURE__*/jsx(MyWalletGrid, {
      ...gridProps
    });
    if (showWelcomeMessage) {
      content = /*#__PURE__*/jsx(MyWalletWelcomeMessage, {
        onGetStartedClick: () => updateMyWallet({
          displayModal: true
        })
      });
    } else if (enableDealPanel) {
      content = /*#__PURE__*/jsx(DelayedImpressionObserver, {
        onChange: () => sendImpressions({
          name: 'myWalletDealBannerImpression'
        }),
        children: /*#__PURE__*/jsx(DealBanner, {
          dealerName: inventory?.owner?.name ?? 'the dealership',
          imgSrc: inventory?.images?.sources?.[0]?.src ?? placeholderImages.noImageAvailable,
          viewDealClickHandler: event => {
            sendClick(myWalletViewDealStatusCTAClick, event, {
              placement: 'receipt'
            });
            updateMyWallet({
              displayModal: true,
              selectedPanelName: 'activeDeals'
            });
          },
          isCompact: sectionProps?.isRightRail,
          onContactOwnerClick: onContactOwnerClick,
          dealVehicle: dealVehicle,
          uiContext: dealBannerUiContext
        })
      }, `dealBanner-${inventoryId}-delayed-impression-observer`);
    }
    return /*#__PURE__*/jsxs(MyWalletGridLight, {
      componentName: sectionComponentName,
      sectionProps: sectionProps,
      showIcon: showIcon,
      "data-cmp": "myWalletGridLightWrapper",
      ...(!showGrid && {
        titleProps: {
          enableTitleLink,
          onClick: handleMyWalletTitleClick
        }
      }),
      children: [!!content && /*#__PURE__*/jsxs("div", {
        className: "padding-4 bg-neutral-secondary border-neutral-tertiary border-top-0 border-horizontal-0",
        children: [displayWalletErrorMessage && /*#__PURE__*/jsx(WalletServicesErrorAlert, {
          className: "margin-horizontal-5"
        }), content, !!walletId && walletIdLabel(true)]
      }), enabledDealNotificationSection && /*#__PURE__*/jsx(DealNotificationSection, {
        inventoryId: inventoryId
      }), children]
    });
  }

  // TODO: delete this block after we are able to add the appropriate theming to Syndicated components
  if (uiContext === 'basic') {
    return /*#__PURE__*/jsxs("div", {
      style: {
        borderRadius: '3px'
      },
      className: clsx('padding-4 margin-vertical-5 bg-primary', {
        'global-theme-enabled': enableGlobalThemeStyles
      }),
      "data-cmp": "myWalletGridContainer",
      children: [/*#__PURE__*/jsxs("div", {
        className: "display-flex justify-content-between align-items-center",
        children: [/*#__PURE__*/jsx(Title, {
          onClick: handleMyWalletDashboardClick,
          uiContext: "basic"
        }), !disableLogo && /*#__PURE__*/jsx(Logo, {})]
      }), /*#__PURE__*/jsxs("div", {
        className: "row margin-top-2",
        children: [/*#__PURE__*/jsxs("div", {
          className: "col-lg-4 col-xs-12 margin-top-2",
          children: [showAlert ? /*#__PURE__*/createElement(SyncMyWalletAlert, {
            ...syncAlert,
            key: "sync-my-wallet-alert"
          }) : /*#__PURE__*/jsx("span", {
            children: "See payments tailored to you, estimated loan rates, and budget tools to customize your shopping experience."
          }), /*#__PURE__*/jsx(Button, {
            className: "margin-top-4 display-block",
            bsStyle: ctaBtnStyle,
            bsSize: "small",
            type: "submit",
            "data-cmp": "btn-update-my-wallet",
            onClick: event => handleUpdateButtonClick(event, {
              label
            }),
            children: label
          })]
        }), showGrid && /*#__PURE__*/jsx("div", {
          className: "col-lg-8 col-xs-12",
          children: /*#__PURE__*/jsx(MyWalletGrid, {
            ...gridProps,
            updatedGrid: false
          })
        })]
      })]
    });
  }
  return /*#__PURE__*/jsxs("div", {
    "data-cmp": "myWalletGridContainer",
    className: clsx({
      'margin-bottom-4': placeWalletGridHigher,
      'margin-bottom-6': !placeWalletGridHigher,
      'global-theme-enabled': enableGlobalThemeStyles
    }, 'padding-top-2'),
    children: [/*#__PURE__*/jsxs("div", {
      className: "margin-bottom-1 display-flex justify-content-between align-items-center flex-wrap-reverse",
      children: [/*#__PURE__*/jsx(Description, {
        isXs: isXs,
        trigger: isLessThanLarge ? ['click'] : ['hover', 'focus', 'click'],
        withKbbGridUpdate: withKbbGridUpdate
      }), /*#__PURE__*/jsx(Title, {
        onClick: handleMyWalletDashboardClick
      })]
    }), showGrid && /*#__PURE__*/jsx(MyWalletGrid, {
      ...gridProps,
      md: 3,
      xs: 6
    }), /*#__PURE__*/jsxs("div", {
      className: "display-flex flex-wrap align-items-baseline"
      // setting a fixed to prevent CLS, height should reserve space for wallet id and alert
      ,

      style: {
        height: 30
      },
      children: [!!walletId && walletIdLabel(false), showAlert && /*#__PURE__*/jsx(SyncMyWalletAlert, {
        ...syncAlert,
        uiContext: "labelAlert"
      })]
    })]
  });
}
export { MyWalletGridContainer as default };
